import React, {useEffect, useState} from 'react';
import Input from '../../../../Assets/Input';
import Dropdown from '../../../../Assets/Dropdown';
import { handleNumberInput } from '../../../../../utils/formUtils';

const GameTotalsFilter = (props) => {
    const [sport, setSport] = useState("");
    const [formType, setFormType] = useState("");
    const [min, setMin] = useState("");
    const [max, setMax] = useState("");
    const [initCrit, setInitCrit] = useState(null);
    const [critIdx, setCritIdx] = useState(null);
    const [errs, setErrs] = useState([]);
    const [errMsg, setErrMsg] = useState(null);
    const sportLabels = {
        mlb: "runs",
        nfl: "points",
        nba: "points"
    };

    const getFilterOptions = () => [
        { value: "between", label: `Total ${sportLabels[sport]} are between` },
        { value: "greater", label: `Total ${sportLabels[sport]} are greater than` },
        { value: "less", label: `Total ${sportLabels[sport]} are less than` }
    ];

    const formFields = {
        min: (
            <div className="relative">
                <Input
                    id="min"
                    type="number"
                    value={min}
                    onChange={(e) => handleNumberInput(e.target.value, setMin)}
                    className={errs.includes('min') ? 'ring-2 ring-brand-red-500 border-brand-red-500' : ''}
                    placeholder=" "
                    aria-label={`Minimum total ${sportLabels[sport]}`}
                />
                <label
                    htmlFor="min"
                    className={`absolute -top-2 left-2 inline-block bg-white dark:bg-gray-900 px-1 text-xs font-medium ${
                        errs.includes('min') ? 'text-brand-red-500' : 'text-gray-900 dark:text-gray-400'
                    }`}
                >
                    {`Minimum total ${sportLabels[sport]}`}
                </label>
            </div>
        ),
        max: (
            <div className="relative">
                <Input
                    id="max"
                    type="number"
                    value={max}
                    onChange={(e) => handleNumberInput(e.target.value, setMax)}
                    className={errs.includes('max') ? 'ring-2 ring-brand-red-500 border-brand-red-500' : ''}
                    placeholder=" "
                    aria-label={`Maximum total ${sportLabels[sport]}`}
                />
                <label
                    htmlFor="max"
                    className={`absolute -top-2 left-2 inline-block bg-white dark:bg-gray-900 px-1 text-xs font-medium ${
                        errs.includes('max') ? 'text-brand-red-500' : 'text-gray-900 dark:text-gray-400'
                    }`}
                >
                    {`Maximum total ${sportLabels[sport]}`}
                </label>
            </div>
        ),
        type: (
            <div>
                <Dropdown
                    id="totals-type"
                    title="Choose filter"
                    items={getFilterOptions()}
                    onSelect={setFormType}
                    activeItem={formType}
                    className={errs.includes('totals-type') ? 'ring-2 ring-brand-red-500' : ''}
                />
            </div>
        )
    };

    const forms = { 
        between: (
            <div className="space-y-4">
                {formFields.min}
                {formFields.max}
            </div>
        ),
        greater: formFields.min,
        less: formFields.max    
    };

    // run when init crit changes
    useEffect(()=> {
        // check if user is editing an existing criteria
        if(props.initCrit){
            const critCopy = {...props.initCrit.crit};
            setFormType(critCopy.rangeType);
            setInitCrit(critCopy);
            setCritIdx(props.initCrit.index);
            switch(critCopy.rangeType){
                case "between":
                    setMin(critCopy.min);
                    setMax(critCopy.max);
                    break;
                case "greater":
                    setMin(critCopy.min);
                    break;
                case "less":
                    setMax(critCopy.max);
                    break;
                default: 
                    break;
            };
        };
    },[props.initCrit])

    // function to check for errors
    const checkErrs = () => {
        let tmpErrs = [];
        let tmpErrMsg = null;
        switch(formType){
            // Between - min and max need to be numbers & min < max
            case "between":
                if(isNaN(min) || min === "" || min < 0){
                    tmpErrs.push("min");
                    tmpErrMsg = `Please enter a positive ${sportLabels[sport]} value`;
                };
                if(isNaN(max) || max === "" || max < 0){
                    tmpErrs.push("max");
                    tmpErrMsg = `Please enter a positive ${sportLabels[sport]} value`;
                };
                if(!isNaN(min) && min !== "" && max > 0 && !isNaN(max) && max !== "" && min > 0){
                    if(min >= max){
                        tmpErrs.push("min");
                        tmpErrs.push("max");
                        tmpErrMsg = `Please ensure the minimum total ${sportLabels[sport]} are less than the maximum`;
                    };
                };
                break;
            case "greater":
                if(isNaN(min) || min === "" || min < 0){
                    tmpErrs.push("min");
                };
                break;
            case "less":
                if(isNaN(max) || max === "" || max < 0){
                    tmpErrs.push("max");
                };
                break;
            default:
                tmpErrs.push("totals-type");
                break; 
        };

        return({errs: tmpErrs, msg: tmpErrMsg});
    };

    // Function to get label for criteria
    const getLabel = () => {
        let label = "";
        switch(formType){
            case "between":
                label = `Total ${sportLabels[sport]} line is between ${min} and ${max}`;
                break;
            case "greater":
                label = `Total ${sportLabels[sport]} line is ${min} or greater`;
                break;
            case "less":
                label = `Total ${sportLabels[sport]} line is ${max} or less`;
                break;
        };
        return label; 
    };

    // Function to get criteria for submission
    const getCrit = () => {
        let crit = {
            type: "line.game-totals",
            rangeType: formType,
        };
        const label = getLabel();
        crit.label = label;
        switch(formType){
            case "between":
                crit.min = min;
                crit.max = max;
                break;
            case "greater":
                crit.min = min;
                break;
            case "less":
                crit.max = max;
                break;
        };
        return crit; 
    };

    // function to check if init criteria has changed
    const checkCritChange = (crit) => {
        let modified = false;
        // no initial criteria passed - set modified to true
        if(critIdx != null){
            // check if range type changed
            if(crit.rangeType !== initCrit.rangeType){
                modified = true;
            // check values for changes 
            }else{
                switch(crit.rangeType){
                    case "between":
                        if(crit.min !== initCrit.min || crit.max !== initCrit.max){
                            modified = true;
                        };
                        break;
                    case "greater":
                        if(crit.min !== initCrit.min){
                            modified = true;
                        };
                        break;
                    case "less":
                        if(crit.max !== initCrit.max){
                            modified = true;
                        };
                        break;
                };
            };
        }else{
            modified = true;
        };
        return modified;
    };

    // set submit function in parent component
    const validateSubmit = (e) => {
        e.preventDefault();
        // check for errors
        const errCheck = checkErrs(); 
        setErrMsg(errCheck.msg);
        setErrs(errCheck.errs);
        if(errCheck.errs.length === 0){
            // check for crit change - returns true if initCrit was modified or not passed
            const crit = getCrit(); 
            const modified = checkCritChange(crit);
            if(modified){
                props.sysContext.changeCrit(crit, critIdx);
                props.sysContext.setCritDisp(null);
            };
            props.sysContext.setStep(2);
        };
    };

    // Run when system context changes - set formType and bet label
    useEffect(()=>{
        const sysSport = props.sysContext.sport;
        setSport(sysSport);
    },[props.sysContext]);

    // run whenever errs changes
    useEffect(()=>{
        // get all elements previously in error state and remove class
        let prevErrs = document.getElementsByClassName("err");
        while(prevErrs.length){
            prevErrs[0].classList.remove("err");
        };
        // add err class based on updated errs
        errs.forEach(err=>{
            const elem = document.getElementById(err);
            if(elem){
                elem.classList.add("err");
            };
        });
    },[errs]);

    return (
        <div className="space-y-6">
            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                <h2 className="text-xl font-semibold leading-6 text-gray-900 dark:text-white">
                    Game Totals Filter
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Set your game totals criteria for filtering bets.
                </p>
            </div>

            <form className="space-y-6">
                {formFields.type}
                {formType !== "" && forms[formType]}
                
                {errMsg && (
                    <p className="mt-2 text-sm text-brand-red-500 dark:text-brand-red-400">
                        {errMsg}
                    </p>
                )}

                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => props.cancel()}
                        className="rounded-md bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={(e) => validateSubmit(e)}
                        className="rounded-md bg-brand-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500"
                    >
                        Save Criteria
                    </button>
                </div>
            </form>
        </div>
    );
};

export default GameTotalsFilter;